/* eslint-disable no-nested-ternary */
// noinspection JSUnusedGlobalSymbols
import { useMemoRef, useStyles } from 'platform/react/hook'
import ErrorBoundary from 'ui/Error'
import { Box, Card, CardActions, CardContent, CardHeader, Grid, lighten, useTheme } from '@mui/material'
import { cloneElement, useEffect, useMemo, useState } from 'react'
import useACLIterator from 'platform/react/hook/useAclIterator'
import SubbedCountWidget from 'ui/Element/Subbed/Widget/Count'
import { getThemeColor, isNum, PlatformEvent } from 'lib/util'
import SkeletonHOC from 'platform/react/hoc/skeleton'
import IconSvg from 'ui/Element/Icon'
import Text from 'ui/Element/Text'

export const variantMapping = {
  primary: {
    variant: 'outlined', // can also be 'normal'
    background: 'background.main',
    text: 'black.main',
    button: 'primary.main',
    buttonText: 'primary.contrastText',
    icon: 'background.dark',
  },
  secondary: {
    variant: 'outlined',
    background: 'background.main',
    text: 'secondary.contrastText',
    button: 'secondary.main',
    buttonText: 'secondary.contrastText',
    icon: 'background.dark',
  },
  background: {
    variant: 'outlined',
    background: 'background.main',
    text: 'black.main',
    button: 'background.dark',
    buttonText: 'black.main',
    icon: 'background.dark',
  },
}

const styles = (theme, { variant, backgroundImage }) => {
  const variantStyle = variantMapping[variant]

  return {
    root: {
      width: '100%',
      height: '15rem',
      borderRadius: '0.5rem',

      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',

      boxShadow: 'none',

      background: getThemeColor(theme, variantStyle.background),
      borderColor: theme.palette.border.main,
      color: getThemeColor(theme, variantStyle.color),
      '& .MuiTypography-root': {
        color: getThemeColor(theme, variantStyle.color),
      },
      zIndex: 1,

      ...backgroundImage && {
        position: 'relative',
        overflow: 'hidden',

        '&::before': {
          content: '""',
          backgroundImage: `url("assets/${backgroundImage}")`,
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          left: 0,
          backgroundSize: 'contain',
          opacity: 0.1,
          backgroundPosition: 'top -30px right -80px',
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
        },
      },
    },
    header: {
      padding: [['1.375rem', '1rem', '0.5rem', '1rem']], // 22px, 16px, 8px, 16px
      flexDirection: 'row-reverse',
      '& .MuiCardHeader-action': {
        alignSelf: 'auto',
      },
      '& .MuiIcon-root': {
        color: theme.palette.white.main,
      },
    },
    icon: {
      padding: '0.25rem',
      borderRadius: '0.24rem',
      backgroundColor: getThemeColor(theme, variantStyle.icon),
    },
    content: {
      display: 'flex',
      alignItems: 'end',
      padding: '1rem',
      '&:last-child': {
        padding: [[0, '1rem', '1rem']],
      },
    },
    count: {
      fontSize: '4.5rem', // 72px
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: 'normal',
      textTransform: 'uppercase',
    },
    label: {
      padding: '0.5rem',
      paddingBottom: '0.8rem',
      wordSpacing: '9999rem',
      textTransform: 'capitalize',
    },
    footer: {
      padding: [[0, '1rem', '1rem']],
    },
  }
}

/**
 * Return the appropriate label based on `count`.
 *
 * @param {string} key            the key, like 'title' or 'body'
 * @param {string} label          the actual label
 * @param {boolean|number} count  the number of items (0, 1, >1)
 * @returns {string}
 */
export const countToLabel = (key, label, count) => {
  if (key === 'footer' || key === 'title') {
    return label[key]
  }

  switch (count) {
    case 0:
      return label[key][`${key}_zero`]
    case 1:
      return label[key][`${key}_one`]
    default:
      return label[key][`${key}_other`]
  }
}

/**
 * CountWidget Component
 *
 * Widget for displaying the count of a resource.
 *
 * @param {Object} p
 * @param {React.Ref} p.forwardedRef                              forwarded ref
 * @param {Object} p.events                                       events
 * @param {Gaia.Component.EventHandler} p.events.acl              acl handler
 * @param {Gaia.Component.EventHandler} p.events.onOpen           onOpen handler
 * @param {Gaia.Component.EventHandler} p.events.onClick          onClick handler
 * @param {Gaia.Component.EventHandler} p.events.onPublication    handler for receiving messages
 * @param {Gaia.Component.EventHandler} p.events.onSubscription   handler for initial subscription
 * @param {Function}                    p.events.showSkeleton     callback to show/hide the
 *                                                                skeleton
 * @param {IconType} p.icon                                       icon to display
 * @param {Object|number} p.count                                 the initial count
 * @param {'primary'|'secondary'|'background'} p.variant          color variant of the widget
 * @param {string} p.backgroundImage                              path to an image
 * @returns {JSX.Element|null}
 */
export const CountWidget = ({
  forwardedRef,
  events,
  icon,
  count: initialCount,
  variant = 'primary',
  backgroundImage,
  ...props
}) => {
  const theme = useTheme()
  const classes = useStyles(styles, { variant, backgroundImage })()
  const [dataCount, setDataCount] = useState(false)

  const count = useMemo(() => (
    dataCount !== false
      ? dataCount
      : isNum(initialCount)
        ? initialCount
        : false), [dataCount, initialCount])

  useEffect(() => {
    (async () => {
      const targetEvent = events?.onPublication || events?.onOpen
      const result = await targetEvent?.(new PlatformEvent('open', {}))

      setDataCount(result?.total?.count ?? 0)
    })()
  }, [])

  useEffect(() => {
    // Weird structure because we're using {@link CountHOC}, and it was made for cells initially
    const newCount = initialCount?.[[props.documentType]]?.[[props.documentType]]?.count
    newCount
      && isNum(newCount)
      && newCount !== 0
      && setDataCount(newCount)
  }, [initialCount])

  useEffect(() => {
    count !== false && events?.showSkeleton?.(false)
  }, [count])

  return !isNum(count) ? null : (
    <ErrorBoundary>
      <Grid
        container
        ref={forwardedRef}
      >
        <Card
          className={classes.root}
          variant={variantMapping[variant].variant}
        >
          <CardHeader
            className={classes.header}
            action={
              <Text
                variant={'18/bold'}
              >
                {props.label.title}
              </Text>
            }
          />
          <Box
            sx={{ cursor: 'pointer' }}
            onClick={e => events.onClick(e, null)}
          >
            <CardContent className={classes.content}>
              <Text
                key={'count'}
                value={count.toString()}
                className={classes.count}
              />
              <Text
                key={'label'}
                variant={'14/medium'}
                className={classes.label}
                value={countToLabel('body', props.label, true)}
              />
            </CardContent>
          </Box>
          <CardActions className={classes.footer}>
            {useACLIterator(props.children, { acl: events.acl, Template: Grid })
              .map((action, i) => {
                const target = action.props?.child || action
                const tProps = target.props
                const textColor = getThemeColor(theme, variantMapping[variant].text)
                const buttonColor = getThemeColor(theme, variantMapping[variant].button)
                const buttonTextColor = getThemeColor(theme, variantMapping[variant].buttonText)

                return cloneElement(target, {
                  key: i,
                  ...tProps,
                  ...tProps.endIconProps && {
                    endIconProps: { ...tProps.endIconProps, color: textColor },
                  },
                  ...tProps.startIconProps && {
                    startIconProps: { ...tProps.startIconProps, color: textColor },
                  },
                  ...tProps.iconProps && {
                    iconProps: { ...tProps.iconProps, color: textColor },
                  },
                  // The button for the widget has some unique styles that are not covered by our
                  // button component, so we need to overwrite those
                  style: {
                    borderRadius: '0.5rem',
                    justifyContent: 'space-between',

                    color: buttonTextColor,
                    backgroundColor: buttonColor,
                    '&:hover': {
                      backgroundColor: `${lighten(buttonColor, 0.1)}!important`,
                    },
                  },
                  value: countToLabel('footer', props.label, true),
                })
              })}
          </CardActions>
        </Card>
      </Grid>
    </ErrorBoundary>
  )
}

export default useMemoRef((props) => {
  const TargetComponent = props.documentType
    ? SubbedCountWidget(SkeletonHOC(CountWidget))
    : SkeletonHOC(CountWidget)

  return <TargetComponent {...props}/>
}, props => [props.count])
